import { useCallback, useEffect, useState } from 'react';
import type { MfaStatusResponse } from '@verybigthings/trackframe-shared/schemas';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hub } from '@/lib/hub-client';
import { CodeInput, EnrolScan, RecoveryCodesPanel, mfaErrorMessage } from '@/pages/auth/Mfa';

/**
 * HUB-121 D3 / D8 — the signed-in user's second factor, on /me/account.
 * Enrol (password → QR → code → recovery codes), regenerate recovery codes,
 * or turn the factor off; the last two need a current code or a recovery code.
 */

export const REQUIRED_MESSAGE = 'Your administrator requires an authenticator app to sign in.';
export const DISABLED_MESSAGE = 'Authenticator app turned off.';

type Mode =
  | { kind: 'idle' }
  | { kind: 'password' }
  | { kind: 'scan'; otpauthUri: string; secret: string }
  | { kind: 'codes'; codes: string[] }
  | { kind: 'code'; action: 'regenerate' | 'disable' };

function messageOf(err: unknown, fallback: string): string {
  const code = err instanceof HubClientError ? err.code : undefined;
  if (code === 'invalid_credentials') return 'Password is incorrect.';
  if (code === 'encryption_key_unconfigured') return 'The hub has no encryption key configured; ask your administrator.';
  return mfaErrorMessage(code, (err as Error)?.message ?? fallback);
}

/** Pure summary line; exported for the static pins. */
export function SecuritySummary({ status }: { status: MfaStatusResponse }) {
  if (!status.totp.enrolled) {
    return <p className="text-sm text-muted-foreground">No authenticator app is set up. Add one to require a code at every sign-in.</p>;
  }
  return (
    <p className="text-sm text-muted-foreground">
      Authenticator app on since {new Date(status.totp.confirmedAt ?? '').toLocaleDateString()} · recovery codes left: <strong>{status.recoveryCodesLeft}</strong>
      {status.recoveryCodesLeft === 0 && <span className="text-destructive"> — generate new ones now.</span>}
    </p>
  );
}

export function SecurityCard() {
  const [status, setStatus] = useState<MfaStatusResponse | null>(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [next, me] = await Promise.all([hub.authMfaStatus(), hub.authMe()]);
      setStatus(next);
      setHasPassword(me.user.hasPassword);
    } catch { /* leave the card quiet */ }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const run = async (work: () => Promise<void>, fallback: string) => {
    setError(null);
    setNotice(null);
    setPending(true);
    try { await work(); } catch (err) { setError(messageOf(err, fallback)); } finally { setPending(false); }
  };

  const startEnrol = () => {
    if (hasPassword) { setMode({ kind: 'password' }); return; }
    void run(async () => {
      const r = await hub.authMfaTotpEnroll({ body: {} });
      setMode({ kind: 'scan', otpauthUri: r.otpauthUri, secret: r.secret });
    }, 'Could not start the setup');
  };

  const onPassword = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void run(async () => {
      const r = await hub.authMfaTotpEnroll({ body: { password } });
      setPassword('');
      setMode({ kind: 'scan', otpauthUri: r.otpauthUri, secret: r.secret });
    }, 'Could not start the setup');
  };

  const onConfirm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void run(async () => {
      const r = await hub.authMfaTotpConfirm({ body: { code } });
      setCode('');
      setMode({ kind: 'codes', codes: r.recoveryCodes });
      await load();
    }, 'Could not confirm the code');
  };

  const onCode = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mode.kind !== 'code') return;
    const action = mode.action;
    void run(async () => {
      if (action === 'regenerate') {
        const r = await hub.authMfaRecoveryCodes({ body: { code } });
        setMode({ kind: 'codes', codes: r.recoveryCodes });
      } else {
        await hub.authMfaTotpDisable({ body: { code } });
        setMode({ kind: 'idle' });
        setNotice(DISABLED_MESSAGE);
      }
      setCode('');
      await load();
    }, 'Could not verify the code');
  };

  const cancel = () => { setMode({ kind: 'idle' }); setPassword(''); setCode(''); setError(null); };

  return (
    <section className="max-w-md rounded-lg border bg-card p-5">
      <div className="space-y-1.5">
        <h2 className="text-base font-medium">Authenticator app</h2>
        {status?.required && <p className="text-sm">{REQUIRED_MESSAGE}</p>}
        {status && <SecuritySummary status={status} />}
      </div>
      <div className="mt-4 space-y-4">
        {mode.kind === 'idle' && status && (
          <div className="flex flex-wrap gap-2">
            {status.totp.enrolled ? (
              <>
                <Button type="button" variant="outline" onClick={() => setMode({ kind: 'code', action: 'regenerate' })}>New recovery codes</Button>
                <Button type="button" variant="destructive" onClick={() => setMode({ kind: 'code', action: 'disable' })}>Turn off</Button>
              </>
            ) : (
              <Button type="button" onClick={startEnrol} disabled={pending}>Set up authenticator app</Button>
            )}
          </div>
        )}
        {mode.kind === 'password' && (
          <form className="space-y-3" onSubmit={onPassword}>
            <div className="space-y-2">
              <Label htmlFor="mfa-password">Confirm your password to continue</Label>
              <Input id="mfa-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={pending}>{pending ? 'Working…' : 'Continue'}</Button>
              <Button type="button" variant="ghost" onClick={cancel}>Cancel</Button>
            </div>
          </form>
        )}
        {mode.kind === 'scan' && (
          <form className="space-y-3" onSubmit={onConfirm}>
            <EnrolScan otpauthUri={mode.otpauthUri} secret={mode.secret} />
            <div className="space-y-2">
              <Label htmlFor="mfa-setup-code">Six-digit code</Label>
              <CodeInput id="mfa-setup-code" value={code} onChange={setCode} recovery={false} autoFocus />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={pending}>{pending ? 'Checking…' : 'Confirm'}</Button>
              <Button type="button" variant="ghost" onClick={cancel}>Cancel</Button>
            </div>
          </form>
        )}
        {mode.kind === 'codes' && <RecoveryCodesPanel codes={mode.codes} onContinue={cancel} continueLabel="I saved them" />}
        {mode.kind === 'code' && (
          <form className="space-y-3" onSubmit={onCode}>
            <div className="space-y-2">
              <Label htmlFor="mfa-action-code">
                {mode.action === 'disable' ? 'Enter a code to turn the authenticator app off' : 'Enter a code to get new recovery codes'}
              </Label>
              <CodeInput id="mfa-action-code" value={code} onChange={setCode} recovery={false} autoFocus />
              <p className="text-xs text-muted-foreground">A code from your app, or one of your recovery codes.</p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" variant={mode.action === 'disable' ? 'destructive' : 'default'} disabled={pending}>
                {pending ? 'Working…' : mode.action === 'disable' ? 'Turn off' : 'Generate'}
              </Button>
              <Button type="button" variant="ghost" onClick={cancel}>Cancel</Button>
            </div>
          </form>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
      </div>
    </section>
  );
}
